import Colors from '@constants/Colors';
import { UserCircleIcon } from '@hugeicons/core-free-icons';
import {
  useBulkGetProfiles,
  useCheckIn,
  useGetEvent,
  useGetTicketsByEventId,
  useUndoCheckIn,
} from '@sunrise-ui/api-client';
import { ProfileResult, withAuthGate } from '@sunrise-ui/composites';
import {
  EmptyState,
  Loader,
  PageContainer,
  TextInput,
  useDynamicStyles,
  useSafeLocalSearchParams,
} from '@sunrise-ui/primitives';
import React, { useMemo, useState } from 'react';
import { FlatList, StyleSheet, TouchableHighlight, View } from 'react-native';
import { Checkbox } from 'react-native-paper';

export function CheckInScreen() {
  const styles = useDynamicStyles(createStyles);
  const { eventId } = useSafeLocalSearchParams<{ eventId: string }>();
  const [filter, setFilter] = useState('');

  // Fetch event details
  const { data: event, isLoading: isLoadingEvent } = useGetEvent(eventId);

  // Fetch tickets for this event
  const { data: tickets, isLoading: isLoadingTickets } = useGetTicketsByEventId(
    {
      eventId,
      communityId: event?.community_id,
    },
  );

  // Get unique user IDs from tickets
  const userIds = useMemo(() => {
    if (!tickets) return [];
    return Array.from(new Set(tickets.map((ticket) => ticket.user_id)));
  }, [tickets]);

  // Fetch profiles for all users
  const { data: profiles, isLoading: isLoadingProfiles } =
    useBulkGetProfiles(userIds);

  // Check-in mutations
  const { mutate: checkInMutate } = useCheckIn();
  const { mutate: undoCheckInMutate } = useUndoCheckIn();

  // Filter profiles based on search
  const filteredProfiles = useMemo(() => {
    if (!profiles) return [];

    const filtered = filter
      ? profiles.filter(
          (profile) =>
            profile.first_name?.toLowerCase().includes(filter.toLowerCase()) ||
            profile.last_name?.toLowerCase().includes(filter.toLowerCase()),
        )
      : profiles;

    return filtered.sort((a, b) =>
      `${a.first_name} ${a.last_name}`.localeCompare(
        `${b.first_name} ${b.last_name}`,
      ),
    );
  }, [profiles, filter]);

  const handleAttendeePress = async (userId: string) => {
    const userTicket = tickets?.find((ticket) => ticket.user_id === userId);
    if (!userTicket || !eventId) return;

    const isCheckedIn = userTicket.is_checked_in;

    if (isCheckedIn) {
      undoCheckInMutate({ userId, eventId });
    } else {
      if (userTicket.token) {
        checkInMutate({
          userId,
          token: userTicket.token,
          eventId,
        });
      }
    }
  };

  const isLoading = isLoadingEvent || isLoadingTickets || isLoadingProfiles;

  if (isLoadingEvent) {
    return (
      <PageContainer title="Check In">
        <Loader />
      </PageContainer>
    );
  }

  return (
    <PageContainer title={`Check In - ${event?.name || 'Event'}`}>
      <View style={styles.container}>
        <TextInput
          label="Search for someone..."
          showLabel={false}
          value={filter}
          onChange={setFilter}
          disabled={isLoading}
        />
        <FlatList
          style={styles.resultList}
          contentContainerStyle={styles.resultContentContainer}
          data={filteredProfiles}
          keyExtractor={(item) => item.user_id}
          renderItem={({ item }) => {
            const { user_id } = item;
            const userTicket = tickets?.find(
              (ticket) => ticket.user_id === user_id,
            );
            const isCheckedIn = userTicket?.is_checked_in;

            return (
              <TouchableHighlight
                style={styles.resultItem}
                key={user_id}
                onPress={async () => {
                  await handleAttendeePress(user_id);
                }}
                underlayColor={Colors.dark.opacity20}
              >
                <>
                  <ProfileResult
                    userId={user_id}
                    profile={item}
                    size="small"
                    isOneLineText={true}
                    style={styles.profileResult}
                  />
                  <View style={styles.checkInRow}>
                    <Checkbox.Android
                      status={isCheckedIn ? 'checked' : 'unchecked'}
                    />
                  </View>
                </>
              </TouchableHighlight>
            );
          }}
          ListEmptyComponent={
            isLoading ? (
              <View style={styles.noResultsContainer}>
                <Loader />
              </View>
            ) : (
              <EmptyState icon={UserCircleIcon} text="No attendees." />
            )
          }
        />
      </View>
    </PageContainer>
  );
}

const createStyles = () =>
  StyleSheet.create({
    container: {
      flex: 1,
      gap: 8,
      maxWidth: 800,
      width: '100%',
      alignSelf: 'center',
    },
    resultContentContainer: {
      gap: 4,
    },
    resultList: {
      marginBottom: 32,
    },
    resultItem: {
      display: 'flex',
      flexDirection: 'row',
      gap: 12,
      paddingHorizontal: 16,
      paddingVertical: 12,
      alignItems: 'center',
      justifyContent: 'space-between',
      borderRadius: 25,
    },
    profileResult: {
      flex: 1,
    },
    checkInRow: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    noResultsContainer: {
      height: 52,
      paddingHorizontal: 16,
      justifyContent: 'center',
    },
  });

export default withAuthGate(CheckInScreen);
